import React from "react";
import clsx from "clsx";

const Button = React.forwardRef(({
  children,
  variant = "contained",
  color = "primary",
  size = "medium",
  disabled = false,
  loading = false,
  fullWidth = false,
  className = "",
  type = "button",
  leftIcon,
  rightIcon,
  ...props
}, ref) => {
  // Size classes
  const sizeClasses = {
    small: "px-3 py-1.5 text-xs",
    medium: "px-4 py-2 text-sm",
    large: "px-5 py-2.5 text-base",
  };

  // Color classes for contained variant
  const containedColors = {
    primary: "bg-primary-500 text-white hover:bg-primary-600 focus:ring-primary-500",
    secondary: "bg-dark-200 text-dark-700 hover:bg-dark-300 focus:ring-dark-500 dark:bg-dark-700 dark:text-dark-200 dark:hover:bg-dark-600",
    success: "bg-success-500 text-white hover:bg-success-600 focus:ring-success-500",
    danger: "bg-danger-500 text-white hover:bg-danger-600 focus:ring-danger-500",
    warning: "bg-warning-500 text-dark-900 hover:bg-warning-600 focus:ring-warning-500",
  };

  // Color classes for outlined variant
  const outlinedColors = {
    primary: "border border-primary-500 text-primary-600 hover:bg-primary-50 focus:ring-primary-500 dark:text-primary-400 dark:hover:bg-primary-900/20",
    secondary: "border border-dark-300 text-dark-600 hover:bg-dark-100 focus:ring-dark-500 dark:border-dark-600 dark:text-dark-400 dark:hover:bg-dark-800",
    success: "border border-success-500 text-success-600 hover:bg-success-50 focus:ring-success-500 dark:text-success-400 dark:hover:bg-success-900/20",
    danger: "border border-danger-500 text-danger-600 hover:bg-danger-50 focus:ring-danger-500 dark:text-danger-400 dark:hover:bg-danger-900/20",
    warning: "border border-warning-500 text-warning-600 hover:bg-warning-50 focus:ring-warning-500 dark:text-warning-400 dark:hover:bg-warning-900/20",
  };

  // Color classes for text variant
  const textColors = {
    primary: "text-primary-600 hover:bg-primary-50 focus:ring-primary-500 dark:text-primary-400 dark:hover:bg-primary-900/20",
    secondary: "text-dark-600 hover:bg-dark-100 focus:ring-dark-500 dark:text-dark-400 dark:hover:bg-dark-800",
    success: "text-success-600 hover:bg-success-50 focus:ring-success-500 dark:text-success-400 dark:hover:bg-success-900/20",
    danger: "text-danger-600 hover:bg-danger-50 focus:ring-danger-500 dark:text-danger-400 dark:hover:bg-danger-900/20",
    warning: "text-warning-600 hover:bg-warning-50 focus:ring-warning-500 dark:text-warning-400 dark:hover:bg-warning-900/20",
  };

  // Get the appropriate color class based on variant
  const getColorClass = () => {
    switch (variant) {
      case "outlined":
        return outlinedColors[color] || outlinedColors.primary;
      case "text":
        return textColors[color] || textColors.primary;
      case "contained":
      default:
        return containedColors[color] || containedColors.primary;
    }
  };

  const sizeClass = sizeClasses[size] || sizeClasses.medium;
  const colorClass = getColorClass();

  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      className={clsx(
        "inline-flex items-center justify-center font-medium rounded-lg",
        "transition-all duration-200",
        "focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-dark-900",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        variant === "outlined" && "bg-transparent",
        variant === "text" && "bg-transparent",
        colorClass,
        sizeClass,
        fullWidth && "w-full",
        className
      )}
      {...props}
    >
      {loading ? (
        <>
          <svg
            className="animate-spin -ml-1 mr-2 h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          Carregando...
        </>
      ) : (
        <>
          {leftIcon && <span className="mr-2 -ml-1">{leftIcon}</span>}
          {children}
          {rightIcon && <span className="ml-2 -mr-1">{rightIcon}</span>}
        </>
      )}
    </button>
  );
});

Button.displayName = "Button";

export default Button;
